/* istanbul ignore file */
import { v4 as uuidv4 } from "uuid";
import { RapportageState } from "./infra/rapportage-schema";
import { User } from "oidc-client";

// Helper to convert ArrayBuffer to Base64, needed for Web Crypto API
function bufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

export const sendReportRequest = async (
  values: RapportageState,
  adviesdossier: string,
  voorstel: string | undefined | null,
  user: User | undefined | null,
  vestiging: string | undefined,
  preview = false
): Promise<void> => {
  const correlationId = uuidv4();
  const reportData = {
    $type:
      "Intersoftware.Platform.Foundation.Bus.Abstract.Models.NieuwRapport, Intersoftware.Platform.Foundation.Bus.Abstract",
    AdviesdossierId: adviesdossier,
    VoorstelId: voorstel,
    RapportStructuurId: values.structuurId,
    Preview: preview,
    Bearer: `Bearer ${user?.access_token}`,
    VestigingId: vestiging
  };
  const jsonPayload = JSON.stringify(reportData, null, 2);

  const connectionString = process.env.REACT_APP_RAPPORT_CONNECTION_STRING;
  if (!connectionString) {
      throw new Error("REACT_APP_RAPPORT_CONNECTION_STRING environment variable not set.");
  }

  const connectionParts = connectionString.split(';').reduce((acc, part) => {
      const [key, ...valueParts] = part.split('=');
      if (key && valueParts.length > 0) {
        acc[key.trim()] = valueParts.join('=').trim();
      }
      return acc;
  }, {} as Record<string, string>);

  const endpoint = connectionParts.Endpoint.replace("sb://", "https://");
  const keyName = connectionParts.SharedAccessKeyName;
  const keyValue = connectionParts.SharedAccessKey;
  const entityPath = connectionParts.EntityPath;

  const expiry = Math.round(Date.now() / 1000) + 3600;
  const stringToSign = encodeURIComponent(endpoint) + '\n' + expiry;

  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode(keyValue),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
  );

  const signatureBuffer = await crypto.subtle.sign(
      'HMAC',
      key,
      encoder.encode(stringToSign)
  );

  const signature = bufferToBase64(signatureBuffer);
  const sasToken = `SharedAccessSignature sr=${encodeURIComponent(endpoint)}&sig=${encodeURIComponent(signature)}&se=${expiry}&skn=${keyName}`;

  const headers = {
      "Authorization": sasToken,
      "Content-Type": "application/json",
      "BrokerProperties": JSON.stringify({ CorrelationId: correlationId })
  };

  const url = `${endpoint}${entityPath}/messages`;

  const response = await fetch(url, {
      method: 'POST',
      headers,
      body: jsonPayload
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Failed to send report request: ${response.status} ${response.statusText}. Body: ${errorBody}`);
  }
};
